import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSignature, Download, CheckCircle2, MessageSquarePlus, PencilLine, RefreshCcw, Link2 } from "lucide-react";
import { api, useFetch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, StatusChip, LoadingState, EmptyState } from "@/components/app/primitives";
import { LiveBadge } from "@/components/app/livePrimitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const STATUS_TONE = { generating: "ai", draft: "risk", active: "validated", failed: "danger" };
const APPROVERS = ["six_lead", "sponsor"];
const backendUrl = process.env.REACT_APP_BACKEND_URL;

const RequestForm = ({ files, findings, onCreated }) => {
  const { roleKey } = useApp();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    process_name: "", audience: "", objective: "", approval_owner: "",
    detail_level: "operational - step level", request: "",
    source_file_ids: [], related_finding_ids: [], sample_sop_file_id: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, id) => setForm((f) => ({
    ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id],
  }));

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/live/sops", { ...form, actor_role: roleKey });
      toast.success("SOP generation started - the draft appears in the register when ready");
      onCreated(res.data.id);
      setForm((f) => ({ ...f, process_name: "", request: "" }));
    } catch (e) { toast.error("Request failed"); }
    setBusy(false);
  };

  return (
    <Panel title="New SOP request" testId="sop-request-panel">
      <div className="grid md:grid-cols-2 gap-2.5 mb-2.5">
        {[["process_name", "Process name"], ["audience", "Audience"], ["objective", "Objective"], ["approval_owner", "Approval owner (named human)"]].map(([k, l]) => (
          <div key={k}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1">{l}</div>
            <Input value={form[k]} onChange={(e) => set(k, e.target.value)}
              className="h-8 text-xs bg-[--shell-3] border-[--hairline]" data-testid={`sop-field-${k}`} />
          </div>
        ))}
      </div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1">Natural-language request</div>
      <Textarea value={form.request} onChange={(e) => set("request", e.target.value)}
        placeholder='e.g. "Create a Brightspeed SOP for proactive intervention on at-risk D2D orders. Use the attached call scripts, cancellation data, and approved escalation paths."'
        className="text-xs bg-[--shell-3] border-[--hairline] min-h-[70px] mb-2.5" data-testid="sop-request-textarea" />

      <div className="grid md:grid-cols-2 gap-3 mb-2.5">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1">Source files ({form.source_file_ids.length} selected)</div>
          <div className="space-y-1 max-h-32 overflow-y-auto thin-scroll">
            {(files || []).map((f) => (
              <label key={f.id} className="flex items-center gap-2 text-[11px] text-[--tx-2] cursor-pointer hover:text-[--tx-1]">
                <input type="checkbox" checked={form.source_file_ids.includes(f.id)} onChange={() => toggle("source_file_ids", f.id)}
                  className="accent-[#B01212]" data-testid={`sop-source-${f.id}`} />
                <span className="truncate">{f.filename}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1">Related findings ({form.related_finding_ids.length} selected)</div>
          <div className="space-y-1 max-h-32 overflow-y-auto thin-scroll">
            {(findings || []).slice(0, 10).map((f) => (
              <label key={f.finding_id} className="flex items-center gap-2 text-[11px] text-[--tx-2] cursor-pointer hover:text-[--tx-1]">
                <input type="checkbox" checked={form.related_finding_ids.includes(f.finding_id)} onChange={() => toggle("related_finding_ids", f.finding_id)}
                  className="accent-[#B01212]" />
                <span className="truncate">{f.title}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 max-w-[340px]">
          <Select value={form.sample_sop_file_id || "__none__"} onValueChange={(v) => set("sample_sop_file_id", v === "__none__" ? "" : v)}>
            <SelectTrigger className="h-8 text-xs bg-[--shell-3] border-[--hairline]" data-testid="sop-sample-select">
              <SelectValue placeholder="Optional: sample SOP to mirror format" />
            </SelectTrigger>
            <SelectContent className="bg-[--shell-2] border-[--hairline]">
              <SelectItem value="__none__" className="text-xs text-[--tx-3]">No format sample</SelectItem>
              {(files || []).filter((f) => f.kind === "document").map((f) => (
                <SelectItem key={f.id} value={f.id} className="text-xs">{f.filename}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button onClick={submit} disabled={busy || !form.process_name || !form.request || !form.approval_owner}
          data-testid="sop-generate-button"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] disabled:opacity-40 active:scale-[0.98] px-3.5 py-2 text-xs font-semibold text-white transition-colors">
          <FileSignature size={12} /> {busy ? "Submitting..." : "Generate draft"}
        </button>
      </div>
      <p className="text-[10px] text-[--tx-3] mt-2">Drafting uses GPT-5.4 grounded in your selected artifacts. Drafts stay <span className="text-[--amber]">Draft</span> until the named approver activates them - activation links an adoption plan into Change Management.</p>
    </Panel>
  );
};

const SopViewer = ({ sopId, onChanged }) => {
  const { roleKey } = useApp();
  const [sop, setSop] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [comment, setComment] = useState("");
  const canApprove = APPROVERS.includes(roleKey);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/live/sops/${sopId}`);
      setSop(res.data);
      setDraft(res.data.content_markdown || "");
    } catch (e) { /* noop */ }
  }, [sopId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (sop?.status !== "generating") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [sop?.status, load]);

  if (!sop) return <LoadingState message="Loading SOP..." />;

  const saveEdit = async () => {
    try {
      await api.patch(`/live/sops/${sopId}`, { content_markdown: draft, change_note: "Manual edit", actor_role: roleKey });
      toast.success("Saved - version bumped");
      setEditing(false); load(); onChanged();
    } catch (e) { toast.error("Save failed"); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.patch(`/live/sops/${sopId}`, { comment, actor_role: roleKey });
      setComment(""); load();
      toast.success("Comment added");
    } catch (e) { toast.error("Comment failed"); }
  };

  const approve = async () => {
    try {
      await api.patch(`/live/sops/${sopId}`, { action: "approve", actor_role: roleKey });
      toast.success("SOP approved and ACTIVE - adoption plan linked to Change Management");
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approval failed"); }
  };

  return (
    <Panel
      title={`${sop.process_name} · v${sop.version}`}
      testId="sop-viewer-panel"
      right={
        <div className="flex items-center gap-2 flex-wrap">
          <StatusChip tone={STATUS_TONE[sop.status]} className="capitalize" data-testid="sop-status-chip">{sop.status}</StatusChip>
          {sop.content_markdown && (
            <>
              <a href={`${backendUrl}/api/live/sops/${sop.id}/export?fmt=docx`} data-testid="sop-export-docx"
                className="inline-flex items-center gap-1 rounded-lg border border-[--hairline] bg-[--shell-3] px-2.5 py-1.5 text-[11px] font-semibold text-[--tx-2] hover:text-[--tx-1] transition-colors">
                <Download size={10} /> DOCX
              </a>
              <a href={`${backendUrl}/api/live/sops/${sop.id}/export?fmt=pdf`} data-testid="sop-export-pdf"
                className="inline-flex items-center gap-1 rounded-lg border border-[--hairline] bg-[--shell-3] px-2.5 py-1.5 text-[11px] font-semibold text-[--tx-2] hover:text-[--tx-1] transition-colors">
                <Download size={10} /> PDF
              </a>
              {!editing && sop.status !== "active" && (
                <button onClick={() => setEditing(true)} data-testid="sop-edit-button"
                  className="inline-flex items-center gap-1 rounded-lg border border-[--hairline] bg-[--shell-3] px-2.5 py-1.5 text-[11px] font-semibold text-[--tx-2] hover:text-[--tx-1] transition-colors">
                  <PencilLine size={10} /> Edit
                </button>
              )}
              {sop.status === "draft" && (
                <button onClick={approve} disabled={!canApprove} data-testid="sop-approve-button"
                  title={canApprove ? "" : "Only SIX Lead or Executive Sponsor can approve"}
                  className="inline-flex items-center gap-1 rounded-lg bg-[--green] hover:brightness-110 disabled:opacity-40 px-2.5 py-1.5 text-[11px] font-semibold text-white transition-colors">
                  <CheckCircle2 size={10} /> Approve & activate
                </button>
              )}
            </>
          )}
        </div>
      }
    >
      {sop.status === "generating" && <LoadingState message="GPT-5.4 is drafting from your artifacts - this view refreshes automatically..." />}
      {sop.status === "failed" && (
        <div className="rounded-lg border border-[--danger] bg-[--danger-soft] p-3 text-xs text-[--tx-1]" data-testid="sop-error">
          Generation failed: {sop.generation_error}
        </div>
      )}

      {sop.content_markdown && !editing && (
        <div className="rounded-lg border border-[--divider] bg-[--panel-dark] p-4 max-h-[52vh] overflow-y-auto thin-scroll" data-testid="sop-content">
          {sop.status !== "active" && (
            <div className="rounded-md border border-[--amber] bg-[--amber-soft] px-3 py-1.5 text-[11px] text-[--amber] font-semibold mb-3">
              DRAFT - not approved for use. Named approver: {sop.approval_owner || "unassigned"}
            </div>
          )}
          <pre className="text-[12px] text-[--tx-2] whitespace-pre-wrap leading-relaxed font-body">{sop.content_markdown}</pre>
        </div>
      )}
      {editing && (
        <div>
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)}
            className="text-[12px] bg-[--shell-3] border-[--hairline] min-h-[44vh] font-mono" data-testid="sop-edit-textarea" />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setEditing(false)} className="rounded-lg border border-[--hairline] px-3 py-1.5 text-xs text-[--tx-2]">Cancel</button>
            <button onClick={saveEdit} data-testid="sop-save-edit-button"
              className="rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] px-3 py-1.5 text-xs font-semibold text-white transition-colors">Save (bump version)</button>
          </div>
        </div>
      )}

      {sop.adoption_plan && (
        <div className="mt-3 rounded-lg border border-[--green] bg-[--green-soft] p-3" data-testid="sop-adoption-plan">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[--green] mb-1.5">
            <Link2 size={11} /> Adoption plan linked to Change Management ({sop.adoption_plan.linked_module})
          </div>
          <div className="grid md:grid-cols-2 gap-1 text-[11px] text-[--tx-2]">
            {sop.adoption_plan.steps.map((s) => (
              <div key={s.phase}><span className="font-mono text-[9px] uppercase text-[--tx-3] mr-1.5">{s.phase}</span>{s.action}</div>
            ))}
          </div>
          <div className="text-[10px] text-[--tx-3] mt-1.5">Adoption metrics: {sop.adoption_plan.adoption_metrics.join(" · ")}</div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3 mt-3 pt-3 border-t border-[--divider]">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1.5">Change log</div>
          {(sop.change_log || []).map((c, i) => (
            <div key={`${c.version}-${c.ts || i}`} className="text-[11px] text-[--tx-2] mb-1">
              <span className="font-mono text-[10px] text-[--ai]">v{c.version}</span> · {(c.ts || "").slice(0, 16).replace("T", " ")} · {c.actor}: {c.note}
            </div>
          ))}
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1.5">Comments</div>
          {(sop.comments || []).map((c, i) => (
            <div key={`${c.ts || i}-${c.actor_role}`} className="text-[11px] text-[--tx-2] mb-1"><span className="text-[--ai]">{c.actor_role}</span>: {c.text}</div>
          ))}
          <div className="flex gap-1.5 mt-1.5">
            <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment"
              className="h-7 text-[11px] bg-[--shell-3] border-[--hairline]" data-testid="sop-comment-input" />
            <button onClick={addComment} data-testid="sop-comment-button"
              className="rounded-lg border border-[--hairline] bg-[--shell-3] px-2 text-[--tx-2] hover:text-[--tx-1] transition-colors">
              <MessageSquarePlus size={12} />
            </button>
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default function SOPBuilderPage() {
  const { data: sops, refetch } = useFetch("/live/sops");
  const { data: files } = useFetch("/live/files");
  const { data: findings } = useFetch("/live/findings");
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (!activeId && (sops || []).length > 0) setActiveId(sops[0].id);
  }, [sops, activeId]);

  useEffect(() => {
    const generating = (sops || []).some((s) => s.status === "generating");
    if (!generating) return;
    const t = setInterval(refetch, 4000);
    return () => clearInterval(t);
  }, [sops, refetch]);

  return (
    <div data-testid="live-sop-page" className="space-y-4">
      <PageHeader
        kicker="Live engagement · operating procedures"
        title="SOP Builder"
        subtitle="Turn requirements, findings, and uploaded artifacts into execution-grade Brightspeed operating procedures. Drafts require a named human approver before activation."
        right={
          <div className="flex items-center gap-2">
            <LiveBadge />
            <button onClick={refetch} data-testid="sop-refresh-button"
              className="inline-flex items-center justify-center rounded-lg w-8 h-8 text-[--tx-3] border border-[--hairline] bg-[--shell-3] hover:text-[--tx-1] transition-colors">
              <RefreshCcw size={13} />
            </button>
          </div>
        }
      />

      <div className="grid xl:grid-cols-3 gap-4">
        <div className="space-y-4">
          <RequestForm files={files} findings={findings} onCreated={(id) => { refetch(); setActiveId(id); }} />
          <Panel title="SOP register" testId="sop-register-panel"
            right={<span className="text-[10px] font-mono text-[--tx-3]">{(sops || []).length}</span>}>
            {(sops || []).length === 0 && <EmptyState message="No SOPs yet. Describe the procedure you need above." />}
            <div className="space-y-1.5">
              {(sops || []).map((s) => (
                <button key={s.id} onClick={() => setActiveId(s.id)} data-testid={`sop-row-${s.id}`}
                  className={`w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${s.id === activeId ? "border-[--ai] bg-[--ai-soft]" : "border-[--hairline] bg-[--shell-2] hover:border-[--ai]"}`}>
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-[--tx-1] truncate">{s.process_name}</div>
                    <div className="text-[10px] font-mono text-[--tx-3]">v{s.version} · {(s.created_at || "").slice(0, 10)} · approver: {s.approval_owner || "—"}</div>
                  </div>
                  <StatusChip tone={STATUS_TONE[s.status]} className="capitalize shrink-0">{s.status}</StatusChip>
                </button>
              ))}
            </div>
          </Panel>
        </div>

        <div className="xl:col-span-2">
          {activeId ? (
            <SopViewer sopId={activeId} onChanged={refetch} />
          ) : (
            <Panel testId="sop-empty-viewer"><EmptyState message="Select or create an SOP to view it here." /></Panel>
          )}
        </div>
      </div>
    </div>
  );
}
